/* Alba theme — vanilla JS custom elements. No dependencies. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const smoothstep = (p, e0, e1) => {
    const t = clamp((p - e0) / (e1 - e0 || 1e-6), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };
  const debounce = (fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  const theme = (window.theme = window.theme || {});
  theme.routes = theme.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update', predictiveSearch: '/search/suggest' };
  theme.strings = theme.strings || {};

  theme.toast = (message) => {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(theme._toastTimer);
    theme._toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  };

  theme.fetchSection = async (url, sectionId) => {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) throw new Error('Section request failed: ' + res.status);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  };

  /* ---------------- Overlay backdrop + drawers ---------------- */
  let backdrop;
  const getBackdrop = () => {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'overlay-backdrop';
      document.body.appendChild(backdrop);
      backdrop.addEventListener('click', () => $$('drawer-panel.is-open').forEach((d) => d.close()));
    }
    return backdrop;
  };

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      // The sticky header wrapper is its own stacking context (z-index 50), below the page backdrop
      // (z-index 90). A drawer left inside it opens UNDER the blurred backdrop. Drawers that live in
      // the header are therefore moved to <body>; a re-rendered header replaces its old copy.
      if (this.parentElement !== document.body && this.closest('.section-header-wrap')) {
        if (this.id) {
          Array.from(document.body.children).forEach((el) => { if (el !== this && el.id === this.id) el.remove(); });
        }
        document.body.appendChild(this);
        return;
      }
      if (this.isBound) return;
      this.isBound = true;
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(trigger) {
      $$('drawer-panel.is-open').forEach((d) => d !== this && d.close(true));
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      // Force a style flush so the transition runs from the closed position.
      void this.offsetWidth;
      this.classList.add('is-open');
      getBackdrop().classList.add('is-active');
      document.body.classList.add('has-overlay');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      const focusTarget = $('input[type="search"], [data-drawer-close], a, button', this);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 120);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close(keepBackdrop) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      if (keepBackdrop !== true) {
        getBackdrop().classList.remove('is-active');
        document.body.classList.remove('has-overlay');
      }
      if (this.trigger && this.trigger.setAttribute) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (keepBackdrop !== true && this.trigger.focus) this.trigger.focus({ preventScroll: true });
      }
    }
  }
  define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* ---------------- Header ---------------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.sticky = this.dataset.sticky !== 'false';
      if (!this.sticky) { const wrap = this.closest('.shopify-section'); if (wrap) wrap.style.position = 'relative'; }
      this.lastState = null;
      this.autoHide = this.sticky && this.dataset.autoHide !== 'false';
      this.lastY = window.scrollY;
      this.hidden_ = false;
      this.onScroll = () => {
        const y = window.scrollY;
        const scrolled = y > 24;
        if (scrolled !== this.lastState) { this.lastState = scrolled; this.classList.toggle('is-scrolled', scrolled); }
        if (!this.autoHide) return;
        // Slide away while the visitor scrolls down, come back as soon as they scroll up.
        const delta = y - this.lastY;
        if (Math.abs(delta) < 6) return;
        this.lastY = y;
        const drawerOpen = !!document.querySelector('drawer-panel:not([hidden])');
        const hide = delta > 0 && y > 160 && !drawerOpen && !this.contains(document.activeElement);
        if (hide !== this.hidden_) { this.hidden_ = hide; this.classList.toggle('is-hidden', hide); }
      };
      addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      // Desktop dropdowns: open on hover, close on leave / outside click / Escape.
      $$('details[data-hover-details]', this).forEach((details) => {
        const item = details.parentElement;
        let timer;
        if (matchMedia('(hover: hover)').matches) {
          item.addEventListener('mouseenter', () => { clearTimeout(timer); details.open = true; });
          item.addEventListener('mouseleave', () => { timer = setTimeout(() => { details.open = false; }, 140); });
        }
        details.addEventListener('keydown', (e) => { if (e.key === 'Escape') { details.open = false; $('summary', details).focus(); } });
      });
      document.addEventListener('click', (e) => {
        $$('details[data-hover-details][open]', this).forEach((d) => { if (!d.contains(e.target)) d.open = false; });
      });
    }
    disconnectedCallback() { removeEventListener('scroll', this.onScroll); }
  }
  define('site-header', SiteHeader);

  /* ---------------- Hero video (plays normally, never scrubbed) ----------------
     - The poster image is in the HTML and paints first; the video is attached afterwards.
     - No download at all on data-saver, 2G/3G-class connections, very low memory or reduced motion.
     - Plays only while on screen; the visitor can pause it. */
  class HeroVideo extends HTMLElement {
    connectedCallback() {
      this.video = $('video', this);
      this.toggle = $('[data-hero-toggle]', this);
      if (!this.video) return;
      if (!this.canPlay()) { this.classList.add('is-still'); return; }
      const start = () => this.attach();
      if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true });
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    canPlay() {
      if (reducedMotion.matches) return false;
      const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (c && (c.saveData || /(^|-)2g$|^3g$/.test(c.effectiveType || ''))) return false;
      if (navigator.deviceMemory && navigator.deviceMemory <= 2) return false;
      return true;
    }
    attach() {
      const v = this.video;
      const small = matchMedia('(max-width: 749px)').matches;
      const src = (small && v.dataset.srcSmall) || v.dataset.src;
      if (!src) { this.classList.add('is-still'); return; }
      v.muted = true; v.playsInline = true; v.loop = true;
      v.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
      v.src = src;
      this.userPaused = false;
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.toggle.setAttribute('aria-pressed', String(this.userPaused));
          this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
          this.sync();
        });
      }
      this.visible = true;
      this.io = new IntersectionObserver((entries) => { this.visible = entries[0].isIntersecting; this.sync(); }, { threshold: 0.15 });
      this.io.observe(this);
      document.addEventListener('visibilitychange', () => this.sync());
    }
    sync() {
      const v = this.video;
      if (this.visible && !this.userPaused && !document.hidden) { const p = v.play(); if (p && p.catch) p.catch(() => this.classList.add('is-still')); }
      else v.pause();
    }
  }
  define('hero-video', HeroVideo);

  /* ---------------- Reveal on scroll ---------------- */
  const initReveal = (root = document) => {
    const items = $$('[data-reveal]:not(.is-revealed)', root);
    if (!items.length) return;
    if (theme.reveal === false || reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => io.observe(el));
  };

  /* ---------------- Count up ---------------- */
  class CountUp extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.value || this.textContent).trim();
      const match = raw.match(/^([^\d]*)([\d.,]+)(.*)$/);
      if (!match || reducedMotion.matches) return;
      const [, prefix, numStr, suffix] = match;
      const hasComma = numStr.includes(',');
      const decimals = (numStr.split('.')[1] || '').length;
      const end = parseFloat(numStr.replace(/,/g, ''));
      if (!isFinite(end)) return;
      const format = (n) => {
        let s = n.toFixed(decimals);
        if (hasComma) s = Number(s).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        return prefix + s + suffix;
      };
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const startTime = performance.now();
        const duration = 1400;
        let lastText = '';
        const step = (now) => {
          const t = clamp((now - startTime) / duration, 0, 1);
          const eased = 1 - Math.pow(1 - t, 4);
          const text = format(end * eased);
          if (text !== lastText) { lastText = text; this.textContent = text; }
          if (t < 1) requestAnimationFrame(step); else this.textContent = raw;
        };
        requestAnimationFrame(step);
      }, { threshold: 0.6 });
      io.observe(this);
    }
  }
  define('count-up', CountUp);

  /* ---------------- Quantity input ---------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn || !this.input) return;
        const min = Number(this.input.min || 0);
        const current = Number(this.input.value) || 0;
        const next = btn.dataset.qty === 'plus' ? current + 1 : Math.max(min, current - 1);
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  define('quantity-input', QuantityInput);

  /* ---------------- Cart ---------------- */
  theme.cart = {
    sectionIds() { return $$('cart-items[data-section]').map((el) => el.dataset.section); },
    async request(url, body) {
      const res = await fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ sections: this.sectionIds(), sections_url: location.pathname }, body))
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      return data;
    },
    render(data) {
      if (data.sections) {
        $$('cart-items[data-section]').forEach((el) => {
          const html = data.sections[el.dataset.section];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = $('[data-cart-contents]', doc);
          const current = $('[data-cart-contents]', el);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          const freshLabel = $('[data-cart-count-label]', doc);
          const label = $('[data-cart-count-label]', el);
          if (freshLabel && label) label.textContent = freshLabel.textContent;
        });
      }
      const count = typeof data.item_count === 'number' ? data.item_count : null;
      if (count !== null) this.setCount(count);
    },
    setCount(count) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
    },
    async refreshCount() {
      try { const cart = await (await fetch(theme.routes.cart + '.js')).json(); this.setCount(cart.item_count); } catch (e) { /* non-critical */ }
    }
  };

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty]');
        if (input) this.update(Number(input.dataset.line), Math.max(0, Number(input.value) || 0));
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.update(Number(remove.dataset.line), 0); }
      });
      this.addEventListener('change', debounce((e) => {
        const note = e.target.closest('[data-cart-note]');
        if (note) fetch(theme.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
      }, 400));
    }
    async update(line, quantity) {
      this.classList.add('is-loading');
      try {
        const data = await theme.cart.request(theme.routes.cartChange, { line, quantity });
        theme.cart.render(data);
      } catch (err) {
        theme.toast(err.message || theme.strings.cartError);
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  define('cart-items', CartItems);

  /* ---------------- Product form ---------------- */
  /* ---------------- "Added to your cart" panel ---------------- */
  theme.formatMoney = (cents) => {
    const value = (Number(cents) / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const format = theme.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, withCommas).replace(/<[^>]+>/g, '');
  };
  theme.showAdded = (panel, item, trigger) => {
    const set = (sel, text) => { const el = $(sel, panel); if (el) el.textContent = text; return el; };
    set('[data-added-title]', item.product_title || item.title || '');
    const variant = $('[data-added-variant]', panel);
    if (variant) { const show = item.variant_title && !/default title/i.test(item.variant_title); variant.hidden = !show; variant.textContent = show ? item.variant_title : ''; }
    set('[data-added-qty]', String(item.quantity || 1));
    const original = item.original_price != null ? item.original_price : item.price;
    set('[data-added-price]', theme.formatMoney(original));
    const final = $('[data-added-final]', panel);
    if (final) {
      const discounted = item.final_price != null && item.final_price < original;
      final.hidden = !discounted;
      final.textContent = discounted ? theme.formatMoney(item.final_price) + (item.discounts && item.discounts[0] ? ' · ' + item.discounts[0].title : '') : '';
    }
    const wrap = $('[data-added-image-wrap]', panel); const img = $('[data-added-image]', panel);
    const src = item.image || (item.featured_image && item.featured_image.url);
    if (wrap && img) { wrap.hidden = !src; if (src) { img.src = src.replace(/(\.[a-z]+)(\?.*)?$/i, '$1$2') ; img.alt = item.product_title || ''; } }
    const bubble = document.querySelector('[data-cart-count]');
    set('[data-added-count]', bubble && bubble.textContent ? bubble.textContent.trim() : String(item.quantity || 1));
    panel.open(trigger);
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const button = $('[type="submit"]', this.form);
      const error = $('[data-form-error]', this);
      if (button.disabled || button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      if (error) error.hidden = true;
      try {
        const formData = new FormData(this.form);
        const sections = theme.cart.sectionIds();
        if (sections.length) { formData.append('sections', sections.join(',')); formData.append('sections_url', location.pathname); }
        const res = await fetch(theme.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
        const panel = $('#AddedPanel');
        if (theme.cartType === 'page' && theme.cartAddAction === 'panel' && panel) {
          await theme.cart.refreshCount();
          theme.showAdded(panel, data, button);
          return;
        }
        if (theme.cartType === 'page' || !$('#CartDrawer')) { location.href = theme.routes.cart; return; }
        theme.cart.render(data);
        await theme.cart.refreshCount();
        $('#CartDrawer').open(button);
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; } else theme.toast(err.message);
      } finally {
        button.classList.remove('is-loading');
      }
    }
  }
  define('product-form', ProductForm);

  /* ---------------- Variant picker (Section Rendering API) ---------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', () => this.onChange());
    }
    async onChange() {
      const sectionId = this.dataset.section;
      const root = document.querySelector('[data-product-root][data-section="' + sectionId + '"]') || document;
      const ids = $$('fieldset', this).map((fs) => {
        const select = $('select', fs);
        if (select) return select.selectedOptions[0] && select.selectedOptions[0].dataset.optionValueId;
        const checked = $('input:checked', fs);
        return checked && checked.dataset.optionValueId;
      }).filter(Boolean);
      const url = new URL(this.dataset.url, location.origin);
      url.searchParams.set('option_values', ids.join(','));
      root.classList.add('is-loading');
      try {
        const doc = await theme.fetchSection(url.toString(), sectionId);
        ['#Price-' + sectionId, '#BuyButtons-' + sectionId, '#StickyPrice-' + sectionId, '#Sku-' + sectionId, '#PromoPrice-' + sectionId].forEach((sel) => {
          const fresh = $(sel, doc); const current = $(sel);
          if (fresh && current) { current.innerHTML = fresh.innerHTML; if (fresh.hasAttribute('hidden')) current.setAttribute('hidden', ''); else current.removeAttribute('hidden'); }
        });
        const freshPicker = $('variant-picker', doc);
        if (freshPicker) {
          const focusedId = document.activeElement && document.activeElement.id;
          this.innerHTML = freshPicker.innerHTML;
          if (focusedId) { const again = document.getElementById(focusedId); if (again) again.focus(); }
        }
        const variantInput = $('#BuyButtons-' + sectionId + ' [data-variant-id]');
        const stickyBtn = $('[data-sticky-submit]');
        const addBtn = $('#BuyButtons-' + sectionId + ' [type="submit"]');
        if (stickyBtn && addBtn) { stickyBtn.disabled = addBtn.disabled; const a = $('[data-atc-text]', addBtn); const b = $('[data-atc-text]', stickyBtn); if (a && b) b.textContent = a.textContent; }
        if (variantInput && variantInput.value) {
          const pageUrl = new URL(location.href);
          pageUrl.searchParams.set('variant', variantInput.value);
          history.replaceState({}, '', pageUrl.toString());
        }
        const freshGallery = $('media-gallery', doc);
        const gallery = $('#Gallery-' + sectionId);
        const featured = freshGallery && freshGallery.dataset.featuredMedia;
        if (gallery && featured) gallery.showMedia(featured);
      } catch (err) {
        theme.toast(theme.strings.cartError || 'Something went wrong');
      } finally {
        root.classList.remove('is-loading');
      }
    }
  }
  define('variant-picker', VariantPicker);

  /* ---------------- Media gallery ---------------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.thumbs = $$('[data-thumb-id]', this);
      if (!this.track) return;
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumbId)));
      let raf = null;
      this.track.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const index = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
          const item = this.track.children[index];
          if (item) this.setActive(item.dataset.mediaId);
        });
      }, { passive: true });
    }
    setActive(id) {
      if (this.activeId === id) return;
      this.activeId = id;
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.thumbId === id));
    }
    showMedia(id) {
      const item = $('[data-media-id="' + id + '"]', this.track);
      if (!item) return;
      this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.setActive(String(id));
    }
  }
  define('media-gallery', MediaGallery);

  /* ---------------- Media lightbox (full screen + zoom) ---------------- */
  class MediaLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = $('dialog', this);
      this.stage = $('[data-lightbox-stage]', this);
      this.img = $('[data-lightbox-image]', this);
      this.counter = $('[data-lightbox-counter]', this);
      this.level = $('[data-lightbox-reset]', this);
      this.hint = $('[data-lightbox-hint]', this);
      this.gallery = document.getElementById(this.dataset.gallery);
      if (!this.dialog || !this.gallery || !this.dialog.showModal) return;
      this.items = $$('[data-zoom-src]', this.gallery);
      if (!this.items.length) return;
      this.index = 0; this.scale = 1; this.x = 0; this.y = 0;
      this.min = 1; this.max = 5;
      this.pointers = new Map();

      this.items.forEach((item, i) => {
        item.addEventListener('click', () => this.open(i));
        item.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.open(i); } });
      });
      $('[data-lightbox-close]', this).addEventListener('click', () => this.dialog.close());
      $('[data-lightbox-prev]', this).addEventListener('click', () => this.go(-1));
      $('[data-lightbox-next]', this).addEventListener('click', () => this.go(1));
      $('[data-lightbox-in]', this).addEventListener('click', () => this.zoomBy(1.5));
      $('[data-lightbox-out]', this).addEventListener('click', () => this.zoomBy(1 / 1.5));
      this.level.addEventListener('click', () => this.reset(true));
      this.dialog.addEventListener('close', () => { document.documentElement.classList.remove('lightbox-open'); this.img.removeAttribute('src'); });
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(-1);
        else if (e.key === 'ArrowRight') this.go(1);
        else if (e.key === '+' || e.key === '=') this.zoomBy(1.5);
        else if (e.key === '-' || e.key === '_') this.zoomBy(1 / 1.5);
        else if (e.key === '0') this.reset(true);
      });
      this.stage.addEventListener('wheel', (e) => {
        e.preventDefault();
        this.zoomBy(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX, e.clientY, false);
      }, { passive: false });
      this.stage.addEventListener('dblclick', (e) => {
        if (this.scale > 1.05) this.reset(true); else this.zoomBy(2.5, e.clientX, e.clientY);
      });
      this.stage.addEventListener('pointerdown', (e) => this.onDown(e));
      this.stage.addEventListener('pointermove', (e) => this.onMove(e));
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((n) => this.stage.addEventListener(n, (e) => this.onUp(e)));
      if (this.items.length < 2) this.classList.add('is-single');
    }

    open(i) {
      this.show(i);
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('lightbox-open');
      if (this.hint) { this.hint.classList.remove('is-hidden'); clearTimeout(this.hintTimer); this.hintTimer = setTimeout(() => this.hint.classList.add('is-hidden'), 2600); }
    }
    show(i) {
      const n = this.items.length;
      this.index = (i + n) % n;
      const item = this.items[this.index];
      this.img.classList.add('is-loading');
      this.img.onload = () => this.img.classList.remove('is-loading');
      this.img.src = item.dataset.zoomSrc;
      this.img.alt = item.dataset.zoomAlt || '';
      if (this.counter) this.counter.textContent = (this.index + 1) + ' / ' + n;
      this.reset(false);
      const gallery = this.gallery;
      if (gallery && gallery.showMedia) gallery.showMedia(item.dataset.mediaId);
    }
    go(dir) { if (this.items.length > 1) this.show(this.index + dir); }

    reset(animate) { this.scale = 1; this.x = 0; this.y = 0; this.apply(animate); }
    zoomBy(factor, cx, cy, animate) {
      const rect = this.stage.getBoundingClientRect();
      const px = (cx === undefined ? rect.left + rect.width / 2 : cx) - (rect.left + rect.width / 2);
      const py = (cy === undefined ? rect.top + rect.height / 2 : cy) - (rect.top + rect.height / 2);
      const next = clamp(this.scale * factor, this.min, this.max);
      const ratio = next / this.scale;
      // Keep the point under the cursor or fingers fixed while scaling.
      this.x = px - (px - this.x) * ratio;
      this.y = py - (py - this.y) * ratio;
      this.scale = next;
      if (this.scale <= 1.001) { this.scale = 1; this.x = 0; this.y = 0; }
      this.apply(animate !== false);
    }
    bounds() {
      const rect = this.stage.getBoundingClientRect();
      const w = this.img.clientWidth * this.scale, h = this.img.clientHeight * this.scale;
      return { bx: Math.max(0, (w - rect.width) / 2), by: Math.max(0, (h - rect.height) / 2) };
    }
    apply(animate) {
      const b = this.bounds();
      this.x = clamp(this.x, -b.bx, b.bx);
      this.y = clamp(this.y, -b.by, b.by);
      this.img.style.transition = animate && !reducedMotion.matches ? 'transform 0.28s cubic-bezier(0.2, 0.7, 0.2, 1)' : 'none';
      this.img.style.transform = 'translate3d(' + this.x + 'px,' + this.y + 'px,0) scale(' + this.scale + ')';
      this.stage.classList.toggle('is-zoomed', this.scale > 1.001);
      if (this.level) this.level.textContent = Math.round(this.scale * 100) + '%';
    }

    onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.stage.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.start = { x: e.clientX, y: e.clientY, tx: this.x, ty: this.y, moved: false };
      if (this.pointers.size === 2) this.pinch = { dist: this.distance(), scale: this.scale };
      if (e.pointerType !== 'mouse') {
        const now = Date.now();
        if (this.lastTap && now - this.lastTap.t < 300 && Math.hypot(e.clientX - this.lastTap.x, e.clientY - this.lastTap.y) < 30) {
          this.lastTap = null;
          if (this.scale > 1.05) this.reset(true); else this.zoomBy(2.5, e.clientX, e.clientY);
          return;
        }
        this.lastTap = { t: now, x: e.clientX, y: e.clientY };
      }
    }
    onMove(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 2 && this.pinch) {
        const pts = Array.from(this.pointers.values());
        const mx = (pts[0].x + pts[1].x) / 2, my = (pts[0].y + pts[1].y) / 2;
        const target = clamp(this.pinch.scale * (this.distance() / this.pinch.dist), this.min, this.max);
        this.zoomBy(target / this.scale, mx, my, false);
        return;
      }
      if (!this.start) return;
      const dx = e.clientX - this.start.x, dy = e.clientY - this.start.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) this.start.moved = true;
      if (this.scale > 1.001) { this.x = this.start.tx + dx; this.y = this.start.ty + dy; this.apply(false); }
    }
    onUp(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
      if (this.start && this.pointers.size === 0) {
        const dx = e.clientX - this.start.x, dy = e.clientY - this.start.y;
        if (this.scale <= 1.001 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(dx < 0 ? 1 : -1);
        this.start = null;
      } else if (this.pointers.size === 1) {
        const p = Array.from(this.pointers.values())[0];
        this.start = { x: p.x, y: p.y, tx: this.x, ty: this.y, moved: true };
      }
    }
    distance() {
      const pts = Array.from(this.pointers.values());
      return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    }
  }
  define('media-lightbox', MediaLightbox);

  /* ---------------- Product media column: stick without hiding its lower part ---------------- */
  // A sticky column taller than the viewport would keep its bottom off screen, so the offset
  // goes negative by exactly the overflow: the column then rests with its bottom edge in view.
  function initStickyMediaCol() {
    const col = $('.product__media-col');
    if (!col || !('ResizeObserver' in window)) return;
    const update = () => {
      const base = 108, gap = 24;
      const top = Math.min(base, window.innerHeight - col.offsetHeight - gap);
      col.style.setProperty('--media-col-top', top + 'px');
    };
    new ResizeObserver(update).observe(col);
    addEventListener('resize', update, { passive: true });
    update();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initStickyMediaCol); else initStickyMediaCol();

  /* ---------------- Copy a discount code ---------------- */
  class CopyCode extends HTMLElement {
    connectedCallback() {
      const button = $('[data-copy]', this);
      const hint = $('[data-copy-hint]', this);
      if (!button) return;
      const idle = hint ? hint.textContent : '';
      button.addEventListener('click', async () => {
        const code = this.dataset.code || '';
        let done = false;
        try {
          await navigator.clipboard.writeText(code);
          done = true;
        } catch (err) {
          // Older browsers and insecure contexts: fall back to a hidden field.
          const field = document.createElement('textarea');
          field.value = code;
          field.setAttribute('readonly', '');
          field.style.cssText = 'position:fixed;top:-100px;opacity:0';
          document.body.appendChild(field);
          field.select();
          try { done = document.execCommand('copy'); } catch (e) { done = false; }
          field.remove();
        }
        if (!hint) return;
        hint.textContent = done ? (window.theme.strings.promoCopied || 'Copied') : code;
        this.classList.toggle('is-copied', done);
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => {
          hint.textContent = idle;
          this.classList.remove('is-copied');
        }, 2400);
      });
    }
  }
  define('copy-code', CopyCode);

  /* ---------------- Sticky add to cart ---------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = $(this.dataset.target);
      if (!target) return;
      this.hidden = false;
      const io = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      io.observe(target);
      this.addEventListener('click', (e) => {
        if (!e.target.closest('[data-sticky-submit]')) return;
        const form = $('form', target);
        if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }
  }
  define('sticky-atc', StickyAtc);

  /* ---------------- Predictive search ---------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled === 'false') return;
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(() => this.search(), 260));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = theme.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('#PredictiveSearchResults', doc);
        this.results.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (err) { /* aborted or offline */ }
    }
  }
  define('predictive-search', PredictiveSearch);

  /* ---------------- Collection filters ---------------- */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.addEventListener('change', debounce((e) => {
        if (!e.target.closest('[data-facets-form]')) return;
        const form = $('[data-facets-form]', this);
        const params = new URLSearchParams(new FormData(form));
        [...params.entries()].forEach(([k, v]) => { if (v === '') params.delete(k); });
        this.load(location.pathname + '?' + params.toString());
      }, 350));
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facets-link], .pagination a');
        if (!link) return;
        e.preventDefault();
        this.load(link.href, link.closest('.pagination'));
      });
      addEventListener('popstate', () => this.load(location.href, false, true));
    }
    async load(url, scrollTop, skipHistory) {
      this.classList.add('is-loading');
      try {
        const doc = await theme.fetchSection(url, this.sectionId);
        const fresh = $('collection-filters', doc);
        if (fresh) {
          const openDrawer = $('drawer-panel.is-open', this);
          ['[data-facets-results]', '.facets__bar', '.facets__active'].forEach((sel) => {
            const a = $(sel, this); const b = $(sel, fresh);
            if (a && b) a.innerHTML = b.innerHTML;
            else if (!a && b) $('[data-facets-form]', this).insertBefore(b, $('drawer-panel', this) || null);
            else if (a && !b) a.remove();
          });
          if (!openDrawer) { const da = $('.drawer__body', this); const db = $('.drawer__body', fresh); if (da && db) da.innerHTML = db.innerHTML; }
          initReveal(this);
        }
        if (!skipHistory) history.pushState({}, '', url);
        if (scrollTop) this.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      } catch (err) {
        location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  define('collection-filters', CollectionFilters);

  /* ---------------- Product recommendations ---------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.children.length) return;
      const io = new IntersectionObserver(async (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        try {
          const res = await fetch(this.dataset.url);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = $('product-recommendations', doc);
          if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; initReveal(this); } else this.remove();
        } catch (err) { this.remove(); }
      }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(this);
    }
  }
  define('product-recommendations', ProductRecommendations);




  /* ---------------- Localization ---------------- */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => { const form = e.target.closest('form'); if (form) form.submit(); });
    }
  }
  define('localization-form', LocalizationForm);

  /* ---------------- Boot ---------------- */
  const boot = () => {
    initReveal();
    document.addEventListener('visibilitychange', () => document.body.classList.toggle('is-paused', document.hidden));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();
